import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Button, Stack, Group, Badge, Center, Loader, Alert,
  SimpleGrid, Image, Paper, Divider, Title, Textarea, Collapse,
} from '@mantine/core'
import { IconAlertCircle, IconCircleCheck, IconPackage, IconX } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { getPublicHandover, confirmPublicHandover, rejectPublicHandover } from '../api/client'
import SignaturePad from '../components/SignaturePad'

export default function HandoverConfirmPage() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [details, setDetails] = useState<any>(null)
  const [signature, setSignature] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [rejectExpanded, setRejectExpanded] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [rejected, setRejected] = useState(false)

  useEffect(() => {
    if (!token) return
    getPublicHandover(token)
      .then(r => setDetails(r.data))
      .catch(e => setErrorMsg(e.response?.data?.message ?? t('handoverConfirm.loadError', 'This link is invalid or has expired.')))
      .finally(() => setLoading(false))
  }, [token])

  const handleConfirm = async () => {
    if (!signature) {
      notifications.show({ color: 'red', message: t('handoverConfirm.signatureRequired', 'Please sign to confirm receipt') })
      return
    }
    setSubmitting(true)
    try {
      await confirmPublicHandover(token!, signature)
      setConfirmed(true)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'Something went wrong') })
    } finally { setSubmitting(false) }
  }

  const handleReject = async () => {
    setRejecting(true)
    try {
      await rejectPublicHandover(token!, rejectReason)
      setRejected(true)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'Something went wrong') })
    } finally { setRejecting(false) }
  }

  if (loading) {
    return <Center style={{ height: '100vh' }}><Loader /></Center>
  }

  if (errorMsg) {
    return (
      <Center style={{ height: '100vh', padding: 24 }}>
        <Alert icon={<IconAlertCircle size={20} />} color="red" title={t('handoverConfirm.cannotConfirm', 'Unable to Confirm')} style={{ maxWidth: 480 }}>
          {errorMsg}
        </Alert>
      </Center>
    )
  }

  if (confirmed) {
    return (
      <Center style={{ height: '100vh', padding: 24 }}>
        <Stack align="center" gap="sm" style={{ maxWidth: 420 }}>
          <IconCircleCheck size={56} color="#16a34a" />
          <Title order={3} ta="center">{t('handoverConfirm.thankYou', 'Thank you!')}</Title>
          <Text c="dimmed" ta="center">{t('handoverConfirm.confirmedHint', 'Receipt has been confirmed. A copy will be sent to your email.')}</Text>
        </Stack>
      </Center>
    )
  }

  if (rejected) {
    return (
      <Center style={{ height: '100vh', padding: 24 }}>
        <Stack align="center" gap="sm" style={{ maxWidth: 420 }}>
          <IconX size={56} color="#64748b" />
          <Title order={3} ta="center">{t('handoverConfirm.rejectedTitle', 'Declined')}</Title>
          <Text c="dimmed" ta="center">{t('handoverConfirm.rejectedHint', 'You have declined to receive this equipment. The sender has been notified.')}</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Box style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px' }}>
      <Group gap={8} mb="lg">
        <IconPackage size={22} />
        <Title order={3}>ASSET<span style={{ color: '#0d9488' }}>OS</span></Title>
      </Group>

      <Paper withBorder radius="md" p="lg" mb="lg">
        <Text fw={700} size="lg" mb={4}>{t('handoverConfirm.title', 'Confirm Receipt of Equipment')}</Text>
        <Text size="sm" c="dimmed" mb="md">
          {t('handoverConfirm.subtitle', '{{fromUser}} handed over the following to you. Please review and sign to confirm.', { fromUser: details.fromUser })}
        </Text>

        <Divider label={t('handoverConfirm.items', 'Items')} labelPosition="left" mb="sm" />
        <Stack gap={6} mb="md">
          {details.assets?.map((a: any, i: number) => (
            <Group key={i} justify="space-between" style={{ background: '#f8f7f4', borderRadius: 8, padding: '8px 12px' }}>
              <Box>
                <Text size="sm" fw={600}>{a.name}</Text>
                <Text size="xs" c="dimmed">
                  {a.assetCode}{a.serialNumber ? ` · S/N ${a.serialNumber}` : ''}
                  {[a.brand, a.model].filter(Boolean).join(' ') ? ` · Brand / Model: ${[a.brand, a.model].filter(Boolean).join(' ')}` : ''}
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>

        {(details.toPosition || details.department || details.location) && (
          <>
            <Divider label={t('handoverConfirm.details', 'Details')} labelPosition="left" mb="sm" />
            <SimpleGrid cols={2} mb="md">
              {details.toPosition && <Box><Text size="xs" c="dimmed">{t('common.position', 'Position')}</Text><Text size="sm">{details.toPosition}</Text></Box>}
              {details.department && <Box><Text size="xs" c="dimmed">{t('common.department', 'Department')}</Text><Text size="sm">{details.department}</Text></Box>}
              {details.location && <Box><Text size="xs" c="dimmed">{t('common.location', 'Location')}</Text><Text size="sm">{details.location}</Text></Box>}
            </SimpleGrid>
          </>
        )}

        {details.assetCondition && (
          <Box mb="sm">
            <Text size="xs" c="dimmed">{t('handover.condition', 'Condition')}</Text>
            <Text size="sm">{details.assetCondition}</Text>
          </Box>
        )}
        {details.accessories && (
          <Box mb="sm">
            <Text size="xs" c="dimmed">{t('handover.accessories', 'Accessories')}</Text>
            <Text size="sm">{details.accessories}</Text>
          </Box>
        )}
        {details.note && (
          <Box mb="sm">
            <Text size="xs" c="dimmed">{t('common.note', 'Note')}</Text>
            <Text size="sm">{details.note}</Text>
          </Box>
        )}

        {details.photos?.length > 0 && (
          <>
            <Divider label={t('handoverConfirm.photosFromSender', 'Photos from Sender')} labelPosition="left" mb="sm" mt="md" />
            <SimpleGrid cols={3} mb="md">
              {details.photos.map((p: string, i: number) => (
                <Image key={i} src={p} radius="sm" style={{ aspectRatio: '1', objectFit: 'cover' }} />
              ))}
            </SimpleGrid>
          </>
        )}

        {details.fromSignature && (
          <>
            <Divider label={t('handoverConfirm.senderSignature', "Sender's Signature")} labelPosition="left" mb="sm" mt="md" />
            <Image src={details.fromSignature} w={200} fit="contain" mb="md" />
          </>
        )}
      </Paper>

      <Paper withBorder radius="md" p="lg">
        <Text fw={700} mb="sm">{t('handoverConfirm.yourSignature', 'Your Signature')}</Text>
        <SignaturePad label={t('handoverConfirm.signHere', 'Sign here to confirm receipt')} onSave={setSignature} />
        <Button fullWidth mt="lg" size="md" loading={submitting} onClick={handleConfirm}
          style={{ background: '#0f172a' }}>
          {t('handoverConfirm.confirmButton', 'Confirm Receipt')}
        </Button>

        <Group justify="center" mt="md">
          <Button variant="subtle" color="gray" size="xs" onClick={() => setRejectExpanded(v => !v)}>
            {t('handoverConfirm.notMine', "This isn't for me / I can't accept this")}
          </Button>
        </Group>
        <Collapse in={rejectExpanded}>
          <Box mt="sm" p="md" style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <Text size="sm" fw={600} mb={6}>{t('handoverConfirm.rejectTitle', 'Decline this handover')}</Text>
            <Textarea
              placeholder={t('handoverConfirm.rejectReasonPlaceholder', 'Optional — let the sender know why')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              minRows={2}
              mb="sm"
            />
            <Button fullWidth variant="light" color="red" loading={rejecting} onClick={handleReject}>
              {t('handoverConfirm.rejectButton', 'Confirm Decline')}
            </Button>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  )
}
